import { useMemo } from "react";

import { useTranslation } from "react-i18next";

import {
  Divider,
  Form,
  Grid,
  Header,
  Icon,
  Label,
  Message,
  Popup,
} from "semantic-ui-react";
import styled from "styled-components";

import { hasPermission } from "simplydo/core/permissions";
import type { OpenAPI } from "simplydo/interfaces";
import { MarkdownViewer } from "simplydo/react/richtext";

import { useAppSelector } from "store";

import FieldHeader from "components/ideas/FieldHeader";
import { useAssessmentEditor } from "components/ideas/hooks/useAssessmentEditor";
import { useChildIdea } from "components/ideas/hooks/useIdea";

import Assessment from "./Assessment";
import AssessmentFeedback from "./AssessmentFeedback";
import CompanyReport from "./CompanyReport";
import Dropdown from "./Dropdown";
import { FieldProps } from "./FieldProps";
import File from "./File";
import Image from "./Image";
import InfoText from "./InfoText";
import LongText from "./LongText";
import MultiFile from "./MultiFile";
import MultiImage from "./MultiImage";
import NumericalText from "./NumericalText";
import OptionMultiple from "./OptionMultiple";
import RichText from "./RichText";
import ShortText from "./ShortText";
import Slider from "./Slider";
import TableField from "./Table";
import Video from "./Video";
import { fieldVisibilityCheck } from "./fieldCondition";

const BodyContainer = styled(Grid.Column)`
  border: none;
`;

type AssessmentSummaryProps = {
  assessmentSummary:
    | NonNullable<
        NonNullable<OpenAPI.Schemas["IndexedIdea"]["summary"]>["assessment"]
      >[string]
    | undefined;
  sectionId: string;
  fieldId: string;
};

const AssessmentSummary = ({
  assessmentSummary,
  sectionId,
  fieldId,
}: AssessmentSummaryProps) => {
  const { t } = useTranslation();

  const summaryData = assessmentSummary?.[sectionId]?.[fieldId];
  if (!summaryData) {
    return null;
  }

  const content = summaryData.summary!;
  if (!content) {
    return null;
  }

  return (
    <Message size="small">
      <Message.Header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        Automated assessment assistance
        <Popup
          offset={[13, 2]}
          trigger={
            <Icon
              name="question circle"
              size="small"
              style={{ color: "#666" }}
            />
          }
          content={`This is an automated summary for this question based on the ${t("generic.idea")} content. It does not represent a score or critique.`}
        />
      </Message.Header>
      <Divider fitted style={{ marginTop: 4, marginBottom: 4 }} />

      <MarkdownViewer value={`"${content}"`} />
    </Message>
  );
};

const FieldHeaderContainer = styled.div`
  margin-bottom: 5px;
  min-height: 30px;
`;

const WordLimitContainer = styled.div<{ $exceedingWordLimit: boolean }>`
  margin-top: 5px;
  p {
    margin: 0;
    font-size: 10px;
    font-weight: ${({ $exceedingWordLimit }) =>
      $exceedingWordLimit ? 700 : 400};
    color: ${({ $exceedingWordLimit }) =>
      $exceedingWordLimit ? "red" : "black"};
  }
`;

const errorMessages = {
  required: "Please enter a value",
  wordLimit: "You are currently exceeding the word limit",
};

const requiredMessages = {
  dropdown: "Please select a value",
  optionMultiple: "Please select an option",
  image: "Please select an image",
  video: "Please upload a video",
  file: "Please upload a file",
  multiimage: "Please upload an image",
  multifile: "Please upload a file",
  slider: "Please enter a value",
  creditsafereport: "Please select a value",
};

type IdeaCustomFieldProps = {
  field: NonNullable<
    OpenAPI.Schemas["IdeaTemplate"]["body"]
  >[number]["fields"][number];
  section: NonNullable<OpenAPI.Schemas["IdeaTemplate"]["body"]>[number];
  updateIdea: (value: any, path: string[], replace?: boolean) => void;
  setFieldRef?: (ref: HTMLDivElement | null, fieldId: string) => void;
  isEditing?: boolean;
  externalChanges?: { templated: Record<string, any> };
  emptyRequiredField?: boolean;
};

const IdeaCustomField = ({
  field,
  section,
  isEditing,
  updateIdea,
  externalChanges,
  emptyRequiredField,
  setFieldRef,
}: IdeaCustomFieldProps) => {
  const { t } = useTranslation();
  const [idea] = useChildIdea({
    select: (data) => {
      const incorrectFieldInfo = data?.submissibilityStatus?.detail?.[
        section.id
      ]?.incorrectFields?.find((i) => i.id === field.id);
      const wordCountInfo = data?.submissibilityStatus?.wordCounts?.find(
        (w) => w.field === field.id,
      );
      return {
        _id: data._id,
        assessmentSummaries: data.assessmentSummaries,
        challenge: data.challenge,
        ownerChallenge: data.ownerChallenge,
        assessments: data.assessments,
        ownerIdeaTemplate: data.ownerIdeaTemplate,
        incorrectFieldInfo: incorrectFieldInfo,
        wordCountInfo: wordCountInfo,
        templated: {
          [field.id]: data.templated?.[field.id],
          ...Object.fromEntries(
            field.visibilityConditions?.conditions?.map((vc) => [
              vc.field,
              data.templated?.[vc.field],
            ]) || [],
          ),
        },
      };
    },
  });

  const ideaId = idea?._id;
  const challengeId = idea?.challenge;

  const user = useAppSelector((state) => state.user);

  const {
    isAssessingSelectedRound,
    selectedAssessmentRoundId,
    urlAssessmentRoundId,
    assessmentState,
    updateAssessment,
  } = useAssessmentEditor(idea);
  const isAssessingSection =
    section.type === "assessment" && isAssessingSelectedRound;
  const assessmentRound = useMemo(() => {
    return idea?.ownerIdeaTemplate?.assessments?.rounds?.find(
      (ar) => ar.id === (assessmentState?.assessmentRoundId || "legacy"),
    );
  }, [assessmentState, idea]);

  /*
    The API returns a list of incorrect fields (i.e. over the word limit, over character limit, empty when required). We check whether this current field is in the incorrect fields
  */
  const incorrectFieldInfo = idea.incorrectFieldInfo;

  /*
    When the user presses submit, if there's an incorrect field, we highlight that field and scroll to it
  */
  const highlightFieldRequired = useMemo(
    () => !!field.required && emptyRequiredField && !!incorrectFieldInfo,
    [field, emptyRequiredField, incorrectFieldInfo],
  );

  /*
    Get a plain english error message from the incorrect field info
  */
  const errorMessage = useMemo(() => {
    if (!incorrectFieldInfo) return null;
    if (incorrectFieldInfo.type === "required") {
      return requiredMessages[field.type] || errorMessages.required;
    }
    return errorMessages[incorrectFieldInfo.type];
  }, [incorrectFieldInfo, field]);

  /*
    Find an external change from collaboration users
  */
  const externalChange = useMemo(
    () => externalChanges?.templated?.[field.id],
    [externalChanges, field.id],
  );

  const fieldValue = useMemo(() => {
    if (isAssessingSection) {
      if (field.type === "assessment") {
        return assessmentState?.assessment?.[field.id]?.score;
      }

      return assessmentState?.assessment?.[field.id]?.value;
    }
    return idea?.templated?.[field.id];
  }, [idea, field, isAssessingSection, assessmentState]);

  /*
    Word counts are calculated server side as they use a more complicated approach to remove whitespace, punctuation etc. These are returned to the client on each save
  */
  const wordCount = idea?.wordCountInfo?.count || 0;

  const handleAssessmentUpdate = (value: any, path: string[]) => {
    // By default this is "templated" when updateIdea is called, but in assessments we want to update the current `assessment` value.
    path[0] = "assessment";

    // If the path is only 2 items long, we need to add the "value" key to the path so that the resulting object is { assessment: { [field.id]: { value: value } } } which leaves room in the object to add other keys like assessor "notes"
    if (path.length === 2) {
      path.push("value");
    }
    updateAssessment(value, path);
  };

  const handleFieldUpdate =
    isAssessingSection ? handleAssessmentUpdate : updateIdea;

  const assessmentHelperEnabled =
    !!idea?.ownerChallenge?.aiAssessmentHelper &&
    hasPermission(user, "super.viewDashboard");

  const isVisible = fieldVisibilityCheck(idea, field);
  if (!isVisible) {
    return null;
  }

  if (field.type === "infotext") {
    return (
      <BodyContainer
        ref={setFieldRef && ((ref) => setFieldRef(ref, field.id))}
        computer={16}
      >
        {isEditing || isAssessingSection ?
          <InfoText field={field} />
        : null}
      </BodyContainer>
    );
  }

  if (field.type === "row") {
    return (
      <BodyContainer
        ref={setFieldRef && ((ref) => setFieldRef(ref, field.id))}
        computer={16}
      >
        <BodyContainer computer={16} />
      </BodyContainer>
    );
  }

  const fieldProps: FieldProps = {
    field: field,
    ideaId: ideaId,
    challengeId: challengeId,
    fieldValue: fieldValue,
    errorMessage: errorMessage,
    highlightFieldRequired: highlightFieldRequired,
    isEditing:
      isEditing || (isAssessingSection && !assessmentState?.isSubmitted),
    updateIdea: handleFieldUpdate,
    assessmentIsClosed:
      (assessmentState?.assessmentRoundId || "legacy") !== "legacy" ?
        assessmentRound?.assessmentClosed
      : idea?.ownerIdeaTemplate?.assessmentClosed,
    isAssessment: isAssessingSection,
  };

  let fieldComponent = null;
  if (field.type === "shorttext") {
    fieldComponent = <ShortText {...fieldProps} />;
  } else if (field.type === "longtext") {
    fieldComponent = <LongText {...fieldProps} />;
  } else if (field.type === "assessmentFeedback") {
    fieldComponent = <AssessmentFeedback {...fieldProps} />;
  } else if (field.type === "richtext") {
    fieldComponent = <RichText {...fieldProps} />;
  } else if (field.type === "dropdown") {
    fieldComponent = <Dropdown {...fieldProps} />;
  } else if (field.type === "slider") {
    fieldComponent = <Slider {...fieldProps} />;
  } else if (field.type === "optionMultiple") {
    fieldComponent = <OptionMultiple {...fieldProps} />;
  } else if (field.type === "image") {
    fieldComponent = <Image {...fieldProps} />;
  } else if (field.type === "video") {
    fieldComponent = <Video {...fieldProps} />;
  } else if (field.type === "file") {
    fieldComponent = <File {...fieldProps} />;
  } else if (field.type === "multiimage") {
    fieldComponent = <MultiImage {...fieldProps} />;
  } else if (field.type === "multifile") {
    fieldComponent = <MultiFile {...fieldProps} />;
  } else if (field.type === "creditsafereport") {
    fieldComponent = <CompanyReport {...fieldProps} />;
  } else if (isAssessingSelectedRound && field.type === "assessment") {
    fieldComponent = <Assessment {...fieldProps} />;
  } else if (field.type === "numerictext") {
    fieldComponent = <NumericalText {...fieldProps} />;
  } else if (field.type === "table") {
    fieldComponent = <TableField {...fieldProps} />;
  }

  const editingOrAssessing =
    isEditing ||
    (section.type === "assessment" &&
      (isAssessingSection ||
        (!isAssessingSelectedRound && !!urlAssessmentRoundId)));

  return (
    <BodyContainer
      ref={setFieldRef && ((ref) => setFieldRef(ref, field.id))}
      data-testid={`idea-field-${field.id}`}
      className={`field-${field.type}`}
      computer={16}
      style={{
        boxShadow:
          highlightFieldRequired ?
            "0.5px 0.5px 5px rgb(233 11 11 / 94%)"
          : undefined,
        borderRadius: 5,
      }}
    >
      <FieldHeaderContainer>
        <FieldHeader
          ideaId={ideaId}
          field={field}
          updateIdea={updateIdea}
          externalChange={externalChange}
          showDescription={editingOrAssessing}
          showRequiredStatus={isEditing || !!isAssessingSection}
          showImportContent={isEditing}
          importingContentEnabled={
            !idea?.ownerChallenge?.preventIdeaContentImport
          }
        />
      </FieldHeaderContainer>
      <div
        style={{
          borderLeft: !editingOrAssessing ? "3px solid #cccccc" : undefined,
          paddingLeft: !editingOrAssessing ? 15 : undefined,
          marginLeft: !editingOrAssessing ? 5 : undefined,
        }}
      >
        <>
          {assessmentHelperEnabled ?
            <AssessmentSummary
              assessmentSummary={
                idea?.assessmentSummaries?.[selectedAssessmentRoundId]
              }
              sectionId={section.id}
              fieldId={field.id}
            />
          : null}
          {fieldComponent}
          {isAssessingSection && field.type !== "assessmentFeedback" ?
            <Form>
              <Divider hidden />
              {assessmentState?.isSubmitted ?
                <p>
                  <Header size="tiny">Justification or notes</Header>
                  {assessmentState?.assessment?.[field.id]?.notes ?? ""}
                </p>
              : <Form.TextArea
                  key={ideaId}
                  label="Justification or notes"
                  width="16"
                  style={{ minHeight: 115 }}
                  placeholder="Add context and reasoning to your evaluation..."
                  defaultValue={
                    assessmentState?.assessment?.[field.id]?.notes ?? ""
                  }
                  onChange={(_e, { value }) =>
                    handleFieldUpdate(value, ["assessment", field.id, "notes"])
                  }
                  disabled={
                    (
                      (assessmentState?.assessmentRoundId || "legacy") !==
                      "legacy"
                    ) ?
                      assessmentRound?.assessmentClosed
                    : idea?.ownerIdeaTemplate?.assessmentClosed
                  }
                />
              }
            </Form>
          : null}
        </>
      </div>

      {field.wordLimit && isEditing ?
        <WordLimitContainer
          $exceedingWordLimit={field.wordLimit - wordCount < 0}
        >
          <p>
            {t("ideas.actions.wordsRemaining", {
              count: field.wordLimit - wordCount,
            })}
          </p>
        </WordLimitContainer>
      : null}
      {isEditing && highlightFieldRequired ?
        <Label size="small" pointing color="red" basic>
          {errorMessage}
        </Label>
      : null}
    </BodyContainer>
  );
};

const IdeaCustomSection = (props: Omit<IdeaCustomFieldProps, "field">) => {
  const { section } = props;

  return (
    <Grid stackable centered data-testid={`idea-section-${section.id}`}>
      {section.fields?.map((field) => (
        <IdeaCustomField key={field.id} field={field} {...props} />
      ))}
    </Grid>
  );
};

export default IdeaCustomSection;
